import { useState } from 'react';
import type { AdminUserRow } from '@repo/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { DropdownMenuItem } from '@repo/ui/src/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/src/components/ui/tooltip';
import { useStartImpersonation } from '../../hooks/useAdminUsers';

interface ImpersonateMenuItemProps {
  user: AdminUserRow;
}

export function ImpersonateMenuItem({ user }: ImpersonateMenuItemProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const startImpersonation = useStartImpersonation();

  const reason = user.isPlatformAdmin
    ? 'Không thể xem như một quản trị viên nền tảng khác.'
    : user.isPlaceholder
      ? 'Tài khoản chưa có email thật, không có phiên đăng nhập để xem.'
      : !user.org
        ? 'Người dùng này chưa thuộc tổ chức nào.'
        : null;

  if (reason) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Wrapping span: a disabled DropdownMenuItem sets pointer-events:none
                on itself, so the hover that drives the tooltip has to land on this
                ancestor instead. */}
            <span>
              <DropdownMenuItem disabled>Xem như người dùng</DropdownMenuItem>
            </span>
          </TooltipTrigger>
          <TooltipContent>{reason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const displayName = user.fullName ?? user.email;

  return (
    <>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          setConfirmOpen(true);
        }}
      >
        Xem như người dùng
      </DropdownMenuItem>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xem như người dùng</AlertDialogTitle>
            <AlertDialogDescription>
              Mở dashboard dưới tài khoản của <strong>{displayName}</strong> ở tab mới, ở chế độ chỉ
              đọc. Phiên kéo dài 30 phút và được ghi vào nhật ký.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={startImpersonation.isPending}>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              disabled={startImpersonation.isPending}
              onClick={() => startImpersonation.mutate(user.id, { onSuccess: () => setConfirmOpen(false) })}
            >
              Mở
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
